import {
  BASE_ONLY_FONT_KEY_SET,
  COMPANY_FONT_KEY_SET,
} from '@/components/Recipe/FlowComponents/Editor/Designer/LayerPropertyPanel/FontsList';
import { FF_COMPANY_FONTS_FROM_DB, FF_WORKSPACE_CUSTOM_FONTS } from '@/consts/featureFlags';
import { posthog } from '@/providers/posthog';

// The engine reports each font's family key (the `BuiltInFont` variant name, or the resource key for
// custom fonts), so classification is an exact set lookup - no file-name parsing.
const BASE_FONT_KEYS: ReadonlySet<string> = BASE_ONLY_FONT_KEY_SET;
const COMPANY_FONT_KEYS: ReadonlySet<string> = COMPANY_FONT_KEY_SET;

/** True when `familyKey` is a base built-in font. Base fonts are always served from static `/fonts/`. */
export function isBaseFont(familyKey: string): boolean {
  return BASE_FONT_KEYS.has(familyKey);
}

/**
 * True when `familyKey` is a company built-in font. Company fonts are served statically until the
 * `company-fonts-from-db` flag is on, then from the workspace font URLs (DB/presigned).
 */
export function isCompanyFont(familyKey: string): boolean {
  return COMPANY_FONT_KEYS.has(familyKey);
}

/**
 * True when `familyKey` is a built-in font (base or company). Built-in fonts always render, so they
 * are never treated as "missing" workspace fonts.
 */
export function isBuiltInFont(familyKey: string): boolean {
  return isBaseFont(familyKey) || isCompanyFont(familyKey);
}

/**
 * Resolves the fetch URL for a font, using static `/fonts/` or the workspace font list.
 * `resourceKey` is the font file name (also the workspace-font-map key); `familyKey` is the font's
 * family identity used to classify it (both come from the engine's `fontsToLoad`).
 */
export function resolveFontFileUrl(
  resourceKey: string,
  familyKey: string,
  workflowCustomFontsUrlsMap: ReadonlyMap<string, string>,
): string {
  // Base fonts always ship statically.
  if (isBaseFont(familyKey)) {
    return '/fonts/' + resourceKey;
  }
  // Company fonts: static until migrated, then DB/presigned. If the workspace isn't backfilled yet,
  // fall back to the (still-present) static file so nothing breaks mid-rollout.
  if (isCompanyFont(familyKey)) {
    if (!posthog.isFeatureEnabled(FF_COMPANY_FONTS_FROM_DB)) {
      return '/fonts/' + resourceKey;
    }
    return workflowCustomFontsUrlsMap.get(resourceKey) ?? '/fonts/' + resourceKey;
  }
  // Custom workspace uploads: load by legacy font file name from the workspace font list.
  // In the next phase of custom-fonts implementation, we'll load the font according to its family, weight and style
  if (!posthog.isFeatureEnabled(FF_WORKSPACE_CUSTOM_FONTS)) {
    return '/fonts/' + resourceKey;
  }
  const url = workflowCustomFontsUrlsMap.get(resourceKey);
  if (!url) {
    throw new Error(`Font file not found for key "${resourceKey}" in the workspace font list`);
  }
  return url;
}
